import React from 'react';
import PropTypes from 'prop-types';
import Measure from 'react-measure';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryTheme,
  VictoryLabel,
  VictoryTooltip,
} from 'victory';

const SELECTED_DATA_COLOR = '#357BFF';
const DEFAULT_DATA_COLOR = '#9ABDFF';
let styles = {
  parent: {
    boxSizing: 'border-box',
    display: 'block',
    padding: 0,
    fontFamily: 'inherit',
  },

  // INDEPENDENT AXIS
  axisX: {
    axis: { stroke: '#C0C0C0', strokeWidth: 1 },
    ticks: {
      size: 5,
      stroke: '#C0C0C0',
      strokeWidth: 1,
    },
    axisLabel: { padding: 25 },
    tickLabels: {
      fontFamily: 'inherit',
      fontSize: 10,
    },
  },

  // DATA SET ONE
  axisY: {
    axis: { stroke: '#C0C0C0', strokeWidth: 0 },
    ticks: { strokeWidth: 0, size: 5 },
    tickLabels: {
      fill: '#2F3B3F',
      fontFamily: 'inherit',
      fontSize: 10,
    },
  },
  graphLine: {
    data: {
      fill: DEFAULT_DATA_COLOR,
    },
  },
  graphTooltip: {
    label: {
      fill: 'white',
      strokeWidth: 1,
      fontFamily: 'inherit',
      fontSize: 12,
    },
    flyout: {
      fill: '#2A3F56',
    },
  },
};

class BarChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      width: props.width || 1,
      height: props.height || 1,
    };
  }

  render() {
    let props = this.props;
    const { width, height } = this.state;
    if (props.xStyle) {
      styles.axisX = props.xStyle;
    }
    if (props.yStyle) {
      styles.axisY = props.yStyle;
    }
    return (
      <Measure
        bounds={true}
        onResize={(contentRect) => {
          this.setState({
            width: props.width || contentRect.bounds.width,
            height: props.height || contentRect.bounds.height,
          });
        }}
      >
        {({ measureRef }) => (
          <div ref={measureRef}>
            <svg
              style={{ ...styles.parent, width: width, height: height }}
              viewBox={`0 20 ${width} ${height + 15}`}
              preserveAspectRatio="xMidYMid meet"
            >
              <VictoryChart
                width={width}
                height={height + 40}
                standalone={false}
                theme={VictoryTheme.material}
                domainPadding={{ x: [100, 0], y: [0, 30] }}
              >
                <VictoryAxis
                  scale="time"
                  standalone={false}
                  style={styles.axisX}
                  tickValues={props.tickValues}
                  fixLabelOverlap={false}
                  label={props.xLabel}
                  axisLabelComponent={
                    props.xLabelPos && <VictoryLabel x={props.xLabelPos.x} y={props.xLabelPos.y} />
                  }
                  tickFormat={props.xTickFormat}
                />
                <VictoryAxis
                  dependentAxis
                  domain={props.domain}
                  orientation="left"
                  style={styles.axisY}
                  standalone={false}
                  label={props.yLabel}
                  axisLabelComponent={
                    props.yLabelPos && <VictoryLabel x={props.yLabelPos.x} y={props.yLabelPos.y} />
                  }
                  tickFormat={props.yTickFormat}
                />
                <VictoryBar
                  data={props.data}
                  domain={props.dataDomain}
                  interpolation="monotoneX"
                  scale={{ x: 'time', y: 'linear' }}
                  standalone={false}
                  style={styles.graphLine}
                  padding={{ left: 20, right: 20 }}
                  labels={({ datum }) => `${props.yTickFormat(datum.y)}`}
                  labelComponent={
                    <VictoryTooltip
                      cornerRadius={2}
                      pointerLength={5}
                      style={styles.graphTooltip.label}
                      flyoutStyle={styles.graphTooltip.flyout}
                      renderInPortal={false}
                    />
                  }
                  events={[
                    {
                      target: 'data',
                      eventHandlers: {
                        onMouseOver: () => {
                          return [
                            {
                              target: 'data',
                              mutation: (props) => {
                                return {
                                  style: { fill: SELECTED_DATA_COLOR },
                                };
                              },
                            },
                            {
                              target: 'labels',
                              mutation: () => ({ active: true }),
                            },
                          ];
                        },
                        onMouseOut: () => {
                          return [
                            {
                              target: 'data',
                              mutation: () => {},
                            },
                            {
                              target: 'labels',
                              mutation: () => ({ active: false }),
                            },
                          ];
                        },
                      },
                    },
                  ]}
                />
              </VictoryChart>
            </svg>
          </div>
        )}
      </Measure>
    );
  }
}

BarChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  xLabel: PropTypes.string,
  xLabelPos: PropTypes.object,
  xStyle: PropTypes.object,
  xTickFormat: PropTypes.func,
  yLabel: PropTypes.string,
  yLabelFormat: PropTypes.func,
  yLabelPos: PropTypes.object,
  yStyle: PropTypes.object,
  yTickFormat: PropTypes.func,
  tickValues: PropTypes.array,
  domain: PropTypes.array,
  data: PropTypes.array,
  dataDomain: PropTypes.object,
};

export default BarChart;
